// Demo backend: the same functions and the same rules as the database, kept in this browser.
import type {
  AppointmentEvent, AppointmentUpsert, B2cReturn, CallRow, CollectLogRow, DepotEntry, PositionRow, Profile, ProgramRow, SnapshotRow, StockExchange, StockItemRow, StockRun, StopNoteRow,
} from '../../domain/types';
import { applyToRaw, collectLogRow } from '../../../supabase/functions/_shared/appointmentPayload.ts';
import type { Role } from '../../domain/types';
import { kvDel, kvGet, kvSet, lsGet } from '../kv';
import type { Db } from './types';

const K = 'demo:';
const get = async <T,>(k: string, dflt: T): Promise<T> => {
  const v = await kvGet<T>(K + k);
  return v === undefined ? dflt : v;
};
const set = (k: string, v: unknown) => kvSet(K + k, v);
const now = () => new Date().toISOString();

type StoredAppt = AppointmentUpsert & { changed_at: string };

/** Demo mode signs you in as an admin. To try another role: localStorage['uco.demoRole'] = 'collector', then reload. */
const DEMO_ROLES: Role[] = ['admin', 'planner', 'viewer', 'collector'];
const demoRole = lsGet('uco.demoRole') as Role | null;
const me: Profile = {
  id: 'local-user', email: 'demo@local', full_name: 'Demo user', role: demoRole && DEMO_ROLES.includes(demoRole) ? demoRole : 'admin', approved: true,
};

export function createDemoDb(): Db {
  return {
    local: true,
    async getUser() {
      return { id: me.id, email: me.email };
    },
    async signIn() {},
    async signUp() {
      return { needsConfirm: false };
    },
    async signOut() {},
    async resetPassword() {},
    async updatePassword() {},
    onAuth() {
      return () => undefined;
    },
    async myProfile() {
      return me;
    },
    async listProfiles() {
      return [me];
    },
    async updateProfile() {},
    async fetchAppointments() {
      const m = await get<Record<string, StoredAppt>>('appointments', {});
      return Object.keys(m).map((id) => ({ id, raw: m[id].raw, changed_at: m[id].changed_at }));
    },
    async insertSnapshot(s) {
      const list = await get<SnapshotRow[]>('snapshots', []);
      const id = list.length ? list[list.length - 1].id + 1 : 1;
      list.push({ id, uploaded_at: now(), uploaded_by: me.id, ...s });
      await set('snapshots', list);
      return id;
    },
    async upsertAppointments(rows) {
      const m = await get<Record<string, StoredAppt>>('appointments', {});
      const ev = await get<(AppointmentEvent & { snapshot_id: number })[]>('events', []);
      const t = now();
      rows.forEach((r) => {
        const old = m[r.id];
        if (!old || old.status !== r.status) {
          ev.push({ appointment_id: r.id, old_status: old ? old.status : null, new_status: r.status, source_updated_at: r.updated_at, snapshot_id: r.last_snapshot, seen_at: t });
        }
        m[r.id] = { ...r, first_snapshot: old ? old.first_snapshot : r.first_snapshot, changed_at: t };
      });
      await set('appointments', m);
      await set('events', ev);
    },
    listSnapshots() {
      return get<SnapshotRow[]>('snapshots', []);
    },
    listEvents() {
      return get<AppointmentEvent[]>('events', []);
    },
    async listPrograms() {
      const m = await get<Record<string, ProgramRow>>('programs', {});
      return Object.values(m).sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
    },
    async savePrograms(progs, deletedIds) {
      const m = await get<Record<string, ProgramRow>>('programs', {});
      const t = now();
      (deletedIds || []).forEach((id) => {
        if (m[id] && m[id].status === 'planned') delete m[id];
      });
      (progs || []).forEach((p) => {
        const cur = m[p.id];
        if (cur && cur.status === 'done') return; // a program marked done is locked
        const seen = new Set<string>();
        const base: Pick<ProgramRow, 'status' | 'driver' | 'notes' | 'created_at' | 'created_by' | 'closed_at'> = cur || {
          status: 'planned', driver: '', notes: '', created_at: t, created_by: me.id, closed_at: null,
        };
        m[p.id] = {
          ...base,
          id: p.id, name: p.name || 'Program', color: p.color, plan_date: p.date || null, depot: p.depot || '',
          hidden: !!p.hidden, updated_at: t, updated_by: me.id,
          stops: (p.stops || []).filter((s) => {
            if (!s || !s.id || seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
          }),
        };
      });
      await set('programs', m);
    },
    async updateProgram(id, patch) {
      const m = await get<Record<string, ProgramRow>>('programs', {});
      if (!m[id]) return;
      const p = { ...patch };
      if (p.status === 'done' && m[id].status !== 'done') p.closed_at = now();
      if (p.status === 'planned') p.closed_at = null;
      m[id] = { ...m[id], ...p, updated_at: now() };
      await set('programs', m);
    },
    async closeProgram(id, driver) {
      const m = await get<Record<string, ProgramRow>>('programs', {});
      if (!m[id] || m[id].status !== 'planned') return;
      m[id] = { ...m[id], status: 'done', closed_at: now(), updated_at: now(), driver: (m[id].driver || '').trim() ? m[id].driver : driver };
      await set('programs', m);
    },
    listReturns() {
      return get<B2cReturn[]>('returns', []);
    },
    async replaceReturns(pid, rows) {
      const list = (await get<B2cReturn[]>('returns', [])).filter((r) => r.program_id !== pid);
      const t = now();
      rows.forEach((r) => list.push({ imported_at: t, imported_by: me.id, ...r }));
      await set('returns', list);
    },
    async listPositions() {
      const m = await get<Record<string, Omit<PositionRow, 'appointment_id'>>>('positions', {});
      return Object.keys(m).map((k) => ({ appointment_id: k, ...m[k] }));
    },
    async setPosition(id, lat, lng) {
      const m = await get<Record<string, Omit<PositionRow, 'appointment_id'>>>('positions', {});
      m[id] = { lat, lng, set_at: now() };
      await set('positions', m);
    },
    async deletePosition(id) {
      const m = await get<Record<string, Omit<PositionRow, 'appointment_id'>>>('positions', {});
      delete m[id];
      await set('positions', m);
    },
    listCalls() {
      return get<CallRow[]>('calls', []);
    },
    async addCall(appointmentId, outcome, note) {
      const list = await get<CallRow[]>('calls', []);
      const row: CallRow = { id: list.length ? list[list.length - 1].id + 1 : 1, appointment_id: appointmentId, outcome, note, called_by: me.id, called_at: now() };
      list.push(row);
      await set('calls', list);
      return row;
    },
    async deleteCall(id) {
      await set('calls', (await get<CallRow[]>('calls', [])).filter((c) => c.id !== id));
    },
    async listStopNotes() {
      return Object.values(await get<Record<string, StopNoteRow>>('stopNotes', {}));
    },
    async setStopNote(appointmentId, note, byName) {
      const m = await get<Record<string, StopNoteRow>>('stopNotes', {});
      const row = note ? { appointment_id: appointmentId, note, noted_by_name: byName, noted_at: now() } : null;
      if (row) m[appointmentId] = row;
      else delete m[appointmentId];
      await set('stopNotes', m);
      return row;
    },
    async listStars() {
      const m = await get<Record<string, string>>('stars', {});
      return Object.keys(m).map((k) => ({ client_key: k, starred_at: m[k] }));
    },
    async addStar(clientKey) {
      const m = await get<Record<string, string>>('stars', {});
      m[clientKey] = now();
      await set('stars', m);
    },
    async deleteStar(clientKey) {
      const m = await get<Record<string, string>>('stars', {});
      delete m[clientKey];
      await set('stars', m);
    },
    // ---- stock of gifts
    listDepot() {
      return get<DepotEntry[]>('depot', []);
    },
    async addDepot(kind, items, note, byName) {
      const list = await get<DepotEntry[]>('depot', []);
      const row: DepotEntry = { id: list.length ? list[list.length - 1].id + 1 : 1, kind, items, note, done_by: me.id, done_by_name: byName, done_at: now() };
      list.push(row);
      await set('depot', list);
      return row;
    },
    listRuns() {
      return get<StockRun[]>('runs', []);
    },
    async loadRun(r) {
      const list = await get<StockRun[]>('runs', []);
      const row: StockRun = { ...r, id: list.length ? list[list.length - 1].id + 1 : 1, returned: null, loaded_at: now(), returned_by_name: null, returned_at: null };
      list.push(row);
      await set('runs', list);
      return row;
    },
    async returnRun(id, returned, note, byName) {
      const list = await get<StockRun[]>('runs', []);
      const i = list.findIndex((r) => r.id === id);
      if (i < 0) throw new Error('This van is not in the data.');
      list[i] = { ...list[i], returned, note, returned_by_name: byName, returned_at: now() };
      await set('runs', list);
      return list[i];
    },
    async updateDepot(id, items, note, byName) {
      const list = await get<DepotEntry[]>('depot', []);
      await set('depot', list.map((d) => (d.id === id ? { ...d, items, note, edited_by_name: byName, edited_at: now() } : d)));
    },
    async deleteDepot(id) {
      await set('depot', (await get<DepotEntry[]>('depot', [])).filter((d) => d.id !== id));
    },
    async updateRun(id, patch, byName) {
      const list = await get<StockRun[]>('runs', []);
      await set('runs', list.map((r) => (r.id === id ? { ...r, ...patch, edited_by_name: byName, edited_at: now() } : r)));
    },
    async deleteRun(id) {
      await set('runs', (await get<StockRun[]>('runs', [])).filter((r) => r.id !== id));
      // its exchanges go with it (on delete cascade in the database)
      await set('exchanges', (await get<StockExchange[]>('exchanges', [])).filter((e) => e.run_id !== id));
    },
    listExchanges() {
      return get<StockExchange[]>('exchanges', []);
    },
    async addExchange(e) {
      const list = await get<StockExchange[]>('exchanges', []);
      list.push({ ...e, id: list.length ? list[list.length - 1].id + 1 : 1, done_at: now() });
      await set('exchanges', list);
    },
    async updateExchange(id, e, byName) {
      const list = await get<StockExchange[]>('exchanges', []);
      await set('exchanges', list.map((x) => (x.id === id ? { ...x, ...e, edited_by_name: byName, edited_at: now() } : x)));
    },
    async deleteExchange(id) {
      await set('exchanges', (await get<StockExchange[]>('exchanges', [])).filter((e) => e.id !== id));
    },
    async listStockItems() {
      return Object.values(await get<Record<string, StockItemRow>>('stockItems', {}));
    },
    async saveStockItem(r) {
      const m = await get<Record<string, StockItemRow>>('stockItems', {});
      m[r.item_id] = { ...r, updated_at: now() };
      await set('stockItems', m);
    },
    async listCollectors() {
      return [{ id: me.id, name: me.full_name || me.email || '?' }];
    },
    listCollectLog() {
      return get<CollectLogRow[]>('collectLog', []);
    },
    async updateAppointment(u) {
      const m = await get<Record<string, StoredAppt>>('appointments', {});
      const cur = m[u.id];
      if (!cur) throw new Error('This appointment is not in the team database.');
      const t = now();
      const raw = applyToRaw(cur.raw, u, t);
      if (cur.status !== u.status) {
        const ev = await get<(AppointmentEvent & { snapshot_id: number })[]>('events', []);
        ev.push({ appointment_id: u.id, old_status: cur.status, new_status: u.status, source_updated_at: t, snapshot_id: cur.last_snapshot, seen_at: t });
        await set('events', ev);
      }
      m[u.id] = { ...cur, status: u.status, updated_at: t, raw, changed_at: t };
      const log = await get<CollectLogRow[]>('collectLog', []);
      log.push({ ...collectLogRow(raw, u), id: log.length ? log[log.length - 1].id + 1 : 1, done_by: me.id, done_by_name: me.full_name, done_at: t });
      await set('collectLog', log);
      await set('appointments', m);
      return raw;
    },
    async wipe() {
      await Promise.all(['appointments', 'events', 'snapshots', 'programs', 'returns', 'positions', 'calls', 'stopNotes', 'stars', 'collectLog', 'depot', 'runs', 'stockItems', 'exchanges'].map((k) => kvDel(K + k)));
    },
  };
}
